import { Switch, Route, useLocation, Redirect } from "wouter";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/lib/auth-context";

import DashboardPage from "@/pages/dashboard";
import PortfolioPage from "@/pages/portfolio";
import TradingPage from "@/pages/trading";
import HistoryPage from "@/pages/history";
import WatchlistPage from "@/pages/watchlist";
import SettingsPage from "@/pages/settings";
import VerificationPage from "@/pages/verification";
import SubscriptionPage from "@/pages/subscription";
import OpenAccountPage from "@/pages/open-account";
import TradingAutomationPage from "@/pages/trading-automation";
import LandingPage from "@/pages/landing";
import PricingPage from "@/pages/pricing";
import FeaturesPage from "@/pages/features";
import AboutPage from "@/pages/about";
import LoginPage from "@/pages/login";
import RiskSettingsPage from "@/pages/risk-settings";
import SentimentPage from "@/pages/sentiment";
import MarketMoversPage from "@/pages/market-movers";
import BrokerageSettingsPage from "@/pages/brokerage-settings";
import TermsOfUsePage from "@/pages/TermsOfUse";
import PrivacyPolicyPage from "@/pages/privacy-policy";
import DisclosuresPage from "@/pages/disclosures";
import NotFound from "@/pages/not-found";

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Redirect to="/login" />;
  }

  return <Component />;
}

function LandingRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Redirect to="/dashboard" />;
  }

  return <Component />;
}

function LoginRoute() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Redirect to="/dashboard" />;
  }

  return <LoginPage />;
}

function Router() {
  return (
    <Switch>
      <Route path="/">
        <LandingRoute component={LandingPage} />
      </Route>
      <Route path="/features" component={FeaturesPage} />
      <Route path="/pricing" component={PricingPage} />
      <Route path="/about" component={AboutPage} />
      <Route path="/terms" component={TermsOfUsePage} />
      <Route path="/privacy" component={PrivacyPolicyPage} />
      <Route path="/disclosures" component={DisclosuresPage} />
      <Route path="/login">
        <LoginRoute />
      </Route>
      <Route path="/dashboard">
        <ProtectedRoute component={DashboardPage} />
      </Route>
      <Route path="/portfolio">
        <ProtectedRoute component={PortfolioPage} />
      </Route>
      <Route path="/trading">
        <ProtectedRoute component={TradingPage} />
      </Route>
      <Route path="/automation">
        <ProtectedRoute component={TradingAutomationPage} />
      </Route>
      <Route path="/history">
        <ProtectedRoute component={HistoryPage} />
      </Route>
      <Route path="/watchlist">
        <ProtectedRoute component={WatchlistPage} />
      </Route>
      <Route path="/settings">
        <ProtectedRoute component={SettingsPage} />
      </Route>
      <Route path="/verification">
        <ProtectedRoute component={VerificationPage} />
      </Route>
      <Route path="/subscription">
        <ProtectedRoute component={SubscriptionPage} />
      </Route>
      <Route path="/open-account">
        <ProtectedRoute component={OpenAccountPage} />
      </Route>
      <Route path="/risk-settings">
        <ProtectedRoute component={RiskSettingsPage} />
      </Route>
      <Route path="/sentiment">
        <ProtectedRoute component={SentimentPage} />
      </Route>
      <Route path="/market-movers">
        <ProtectedRoute component={MarketMoversPage} />
      </Route>
      <Route path="/brokerages">
        <ProtectedRoute component={BrokerageSettingsPage} />
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <Router />
          <Toaster />
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
